import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { SnackbarProvider } from 'notistack';
import Landing from './pages/Landing';
import AuthGuard from './components/AuthGuard';
import AppContent from './components/AppContent';
import { CoinProvider } from './contexts/CoinContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { setSubscriptionDialogHandler } from './services/api';
import ErrorPage from './pages/ErrorPage';

const AppContent402Handler = ({ children }) => {
  const { showSubscriptionDialog } = useSubscription();

  useEffect(() => {
    setSubscriptionDialogHandler(showSubscriptionDialog);
  }, [showSubscriptionDialog]);

  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user, backendError } = useAuth();
  const currentPath = window.location.pathname;

  // Show error page for any backend connectivity issues
  if (backendError || user?.is_onboarded === undefined) {
    return <ErrorPage message={backendError || "Unable to connect to the server. Please try again later."} />;
  }

  // Only handle routing based on onboarding status if we have valid user data
  if (user?.firebaseUser?.emailVerified) {
    if (user?.is_onboarded && currentPath === '/onboarding') {
      return <Navigate to="/dashboard" replace />;
    }

    if (!user?.is_onboarded && currentPath !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

const AppWithRouter = () => {
  const navigate = useNavigate();

  return (
    <ThemeProvider>
      <AuthProvider>
        <ProjectProvider>
          <CoinProvider>
            <SubscriptionProvider onNavigate={navigate}>
              <AppContent402Handler>
                <SnackbarProvider 
                  maxSnack={3}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                >
                  <Routes>
                    <Route path="/login" element={<Landing />} />
                    <Route
                      path="/*"
                      element={
                        <AuthGuard>
                          <OnboardingGuard>
                            <AppContent />
                          </OnboardingGuard>
                        </AuthGuard>
                      }
                    />
                  </Routes>
                </SnackbarProvider>
              </AppContent402Handler>
            </SubscriptionProvider>
          </CoinProvider>
        </ProjectProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppWithRouter />
    </BrowserRouter>
  );
}

export default App;
